import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileUpload } from "@/components/ui/file-upload";
import { ROLE_LABELS } from "@/lib/helpers";
import { SignOut } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

/**
 * Self-service profile dialog. Any authenticated user (including guardians /
 * parents) can update their own name, phone, and profile photo through this
 * dialog. Role/branch/compensation changes stay under admin control.
 */
export default function ProfileDialog({ open, onOpenChange }) {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", photo_file_id: null });
  const [saving, setSaving] = useState(false);
  const [showCreds, setShowCreds] = useState(false);
  const [creds, setCreds] = useState({ old_password: "", new_email: "", new_password: "", confirm: "" });
  const [credsSaving, setCredsSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        photo_file_id: user.photo_file_id || null,
      });
    }
  }, [open, user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        name: form.name,
        phone: form.phone,
        photo_file_id: form.photo_file_id,
      });
      await refreshUser?.();
      toast.success("Profile updated");
      onOpenChange?.(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Instant photo save when uploaded/removed so users see the change even if
  // they close the dialog without hitting "Save".
  const onPhotoChange = async (id) => {
    setForm(f => ({ ...f, photo_file_id: id }));
    try {
      await api.patch("/auth/me", { photo_file_id: id });
      await refreshUser?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeCredentials = async () => {
    if (!creds.old_password) { toast.error("Enter your current password"); return; }
    if (!creds.new_email && !creds.new_password) {
      toast.error("Change email, password, or both — nothing to update.");
      return;
    }
    if (creds.new_password && creds.new_password !== creds.confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setCredsSaving(true);
    try {
      const body = { old_password: creds.old_password };
      if (creds.new_email) body.new_email = creds.new_email.trim().toLowerCase();
      if (creds.new_password) body.new_password = creds.new_password;
      const { data } = await api.post("/auth/change-credentials", body);
      // Backend re-issues the httpOnly cookie on email change; nothing
      // to persist client-side.
      await refreshUser?.();
      toast.success(data.token || data.email ? "Credentials updated — you can log in with the new email next time." : "Password updated");
      setCreds({ old_password: "", new_email: "", new_password: "", confirm: "" });
      setShowCreds(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCredsSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-md" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">My profile</DialogTitle>
          <DialogDescription className="text-xs">
            Update your name, phone, and profile photo. Roles and branches are managed by your administrator.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FileUpload
            value={form.photo_file_id}
            onChange={onPhotoChange}
            entityType="user"
            entityId={user?.id}
            accept="image/jpeg,image/png,image/webp"
            variant="avatar"
            size={96}
          />
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              data-testid="profile-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              data-testid="profile-phone"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Email</Label>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Roles</Label>
            <div className="flex flex-wrap gap-1">
              {(user?.roles || []).map(r => (
                <Badge key={r} variant="outline" className="text-[10px]">{ROLE_LABELS[r] || r}</Badge>
              ))}
            </div>
          </div>

          {/* Change credentials (email + password) */}
          <div className="border-t pt-3">
            {!showCreds ? (
              <Button
                type="button" variant="ghost" size="sm"
                onClick={() => setShowCreds(true)}
                className="text-xs text-primary"
                data-testid="show-change-creds"
              >
                Change email or password →
              </Button>
            ) : (
              <div className="space-y-2" data-testid="change-creds-form">
                <div className="text-sm font-medium">Change credentials</div>
                <p className="text-[11px] text-muted-foreground">
                  Enter your current password to prove it&apos;s you, then set a new email and/or password. Leave blank to skip either.
                </p>
                <div className="space-y-1.5">
                  <Label>Current password *</Label>
                  <Input
                    type="password"
                    value={creds.old_password}
                    onChange={e => setCreds(c => ({ ...c, old_password: e.target.value }))}
                    data-testid="creds-old"
                    autoComplete="current-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>New email</Label>
                  <Input
                    type="email"
                    placeholder={user?.email}
                    value={creds.new_email}
                    onChange={e => setCreds(c => ({ ...c, new_email: e.target.value }))}
                    data-testid="creds-new-email"
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>New password</Label>
                  <Input
                    type="password"
                    value={creds.new_password}
                    onChange={e => setCreds(c => ({ ...c, new_password: e.target.value }))}
                    data-testid="creds-new-password"
                    autoComplete="new-password"
                  />
                </div>
                {creds.new_password && (
                  <div className="space-y-1.5">
                    <Label>Confirm new password</Label>
                    <Input
                      type="password"
                      value={creds.confirm}
                      onChange={e => setCreds(c => ({ ...c, confirm: e.target.value }))}
                      data-testid="creds-confirm"
                      autoComplete="new-password"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => { setShowCreds(false); setCreds({ old_password: "", new_email: "", new_password: "", confirm: "" }); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button" size="sm"
                    onClick={changeCredentials}
                    disabled={credsSaving}
                    data-testid="creds-submit"
                  >
                    {credsSaving ? "Saving…" : "Update credentials"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          {/* Mobile only — the desktop sidebar already carries sign-out,
              and below lg that sidebar is hidden. */}
          <Button
            variant="ghost"
            onClick={async () => { onOpenChange?.(false); await logout(); navigate("/login"); }}
            className="lg:hidden sm:mr-auto text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
            data-testid="logout-btn-profile"
          >
            <SignOut size={16} weight="duotone" /> Sign out
          </Button>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Close</Button>
          <Button onClick={save} disabled={saving} data-testid="profile-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
